import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { Archive, LayoutDashboard, LogOut, Rss, Settings } from 'lucide-react';
import { api } from './api/client';
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/LoginPage';
import CollectionsPage from './pages/CollectionsPage';
import SourcesPage from './pages/SourcesPage';
import SettingsPage from './pages/SettingsPage';
import './styles.css';

const AuthContext = createContext(null);

function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .me()
      .then((data) => setUser(data.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login: async (email, password, remember) => {
        const data = await api.login(email, password, remember);
        setUser(data.user);
      },
      logout: async () => {
        await api.logout().catch(() => {});
        setUser(null);
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function RequireAuth({ children }) {
  const auth = useAuth();
  if (auth.loading) return <div className="screen-status">Laden...</div>;
  if (!auth.user) return <Navigate to="/login" replace />;
  return children;
}

function AppShell() {
  const auth = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await auth.logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">SM</span>
          <div>
            <strong>Source Manager</strong>
            <span>Redaktion</span>
          </div>
        </div>
        <nav className="nav">
          <NavLink to="/dashboard">
            <LayoutDashboard size={18} />
            Dashboard
          </NavLink>
          <NavLink to="/collections">
            <Archive size={18} />
            Collections
          </NavLink>
          {auth.user?.role === 'admin' ? (
            <>
              <NavLink to="/sources">
                <Rss size={18} />
                Quellen
              </NavLink>
              <NavLink to="/settings">
                <Settings size={18} />
                Integrationen
              </NavLink>
            </>
          ) : null}
        </nav>
        <div className="sidebar-footer">
          <span>{auth.user?.email}</span>
          <button className="icon-text-button" onClick={handleLogout} type="button">
            <LogOut size={17} />
            Logout
          </button>
        </div>
      </aside>
      <main className="main-panel">
        <Routes>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/collections" element={<CollectionsPage />} />
          <Route path="/sources" element={<SourcesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Router() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage useAuth={useAuth} />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

createRoot(document.getElementById('root')).render(<Router />);
